import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Box, FormControl, MenuItem, Select, InputLabel, useMediaQuery } from '@material-ui/core';
import TimeField from './TimeField';
import Quarter from './Quarter';
import DateFnsUtils from '@date-io/date-fns';
import { disableMonth, dateToCron, getWeeks, datePrefix } from 'helper/utils';
import {
	TIME_PERIOD,
	WEEKS,
	EVERY_MONTH,
	DAILY,
	WEEKLY,
	MONTHLY,
	QUARTERLY,
	LATER
} from 'constants/constant';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import reportSettingStyles from '../ReportSettings.style';
import { UserPortalContext } from 'context/appcontext';
import { FEATURES } from 'helper/basicFeatureFlag';

function Schedule(props) {
	const {
		scheduleType,
		setSelectedDate,
		selectedDate,
		weeksSelected,
		setWeeksSelected,
		dateSelect,
		setDateSelect,
		selectQuarter,
		setSelectQuarter,
		setCronExpression,
		weeks,
		dropDownForWeeks,
		isDynamicSelected
	} = props;
	const isSmallScreen = useMediaQuery('(max-width:360px)');
	const classes = reportSettingStyles({ isSmallScreen });
	const portalUserContext = useContext(UserPortalContext);
	const { customerType } = portalUserContext;

	const { DISABLE_LATER_TIME: disableLaterTimeEntry } = FEATURES[customerType].REPORT_SETTINGS_PAGE;

	const handleDateChange = (date) => {
		setSelectedDate(date);
	};

	const handleSelect = (event) => {
		setWeeksSelected(event.target.value);
	};

	const handleDateSelect = (event) => {
		setDateSelect(event.target.value);
	};

	useEffect(() => {
		const cronExp = dateToCron({
			selectedDateTime: selectedDate,
			weeks: getWeeks({ weekType: weeksSelected, weeks: weeks }),
			quarter: selectQuarter,
			cronType: scheduleType,
			selectedDate: dateSelect,
			isDynamicSelected
		});
		setCronExpression(cronExp);
	}, [selectedDate, dateSelect, weeks, weeksSelected, scheduleType, isDynamicSelected]);

	const renderSwitch = (param) => {
		// Time input should be hidden for Schedule `Later`
		let timeFieldComp = !disableLaterTimeEntry ? (
			<TimeField
				scheduleType={scheduleType}
				selectedDate={selectedDate}
				handleDateChange={handleDateChange}
			/>
		) : null;
		switch (param) {
			case LATER:
				return (
					<MuiPickersUtilsProvider utils={DateFnsUtils}>
						<Box className={`${classes.spaceBetween} ${classes.datePanel}`}>
							<KeyboardDatePicker
								aria-label="Date picker"
								className={classes.dateTimeField}
								KeyboardButtonProps={{ 'aria-label': 'Date Picker' }}
								disableToolbar
								inputVariant="outlined"
								variant="inline"
								format="MM/dd/yyyy"
								id="date-picker-inline"
								label="Target Date"
								value={selectedDate}
								disablePast={true}
								onChange={handleDateChange}
								shouldDisableDate={(date) => disableMonth(date, customerType)}
								InputProps={{
									style: {
										fontSize: isSmallScreen ? 14 : 16,
										fontFamily: 'Bogle-Regular',
										marginRight: '8px'
									},
									readOnly: true
								}}
							/>
							{timeFieldComp}
						</Box>
					</MuiPickersUtilsProvider>
				);
			case DAILY:
				if (isDynamicSelected) {
					return <Box />;
				}
				return (
					<MuiPickersUtilsProvider utils={DateFnsUtils}>
						<Box className={`${classes.spaceBetween} ${classes.datePanel}`}>
							<TimeField
								scheduleType={scheduleType}
								className={classes.timeField}
								selectedDate={selectedDate}
								handleDateChange={handleDateChange}
							/>
						</Box>
					</MuiPickersUtilsProvider>
				);
			case WEEKLY:
				if (isDynamicSelected) {
					return <Box />;
				}
				return (
					<MuiPickersUtilsProvider utils={DateFnsUtils}>
						<Box className={`${classes.spaceBetween} ${classes.datePanel}`}>
							{dropDownForWeeks ? (
								<FormControl variant="outlined" className={classes.dateTimeField}>
									<InputLabel id="week-label">{TIME_PERIOD}</InputLabel>
									<Select
										labelId="week-label"
										id="week-select"
										value={weeksSelected}
										className={classes.select}
										MenuProps={{ classes: { paper: classes.menuPaper } }}
										onChange={handleSelect}
										label="Time Period">
										{WEEKS.map((item) => {
											return (
												<MenuItem
													key={item.keyName}
													className={`${classes.text} ${classes.menuItem}`}
													value={item.keyName}>
													{item.value}
												</MenuItem>
											);
										})}
									</Select>
								</FormControl>
							) : (
								<></>
							)}
							{!isDynamicSelected && (
								<TimeField
									scheduleType={scheduleType}
									selectedDate={selectedDate}
									handleDateChange={handleDateChange}
								/>
							)}
						</Box>
					</MuiPickersUtilsProvider>
				);
			case MONTHLY:
				return (
					<MuiPickersUtilsProvider utils={DateFnsUtils}>
						<Box
							className={`${classes.datePanel}`}
							style={{ display: 'flex', alignItems: 'center' }}>
							<FormControl variant="outlined" className={classes.dateField}>
								<InputLabel id="week-label">Day</InputLabel>
								<Select
									labelId="week-label"
									id="week-select"
									value={dateSelect}
									className={classes.text}
									onChange={handleDateSelect}
									MenuProps={{ classes: { paper: classes.menuPaper } }}
									label="Day">
									{Array.apply(1, Array(31)).map(function (x, i) {
										const date = i + 1;
										return (
											<MenuItem
												className={`${classes.text} ${classes.menuItem}`}
												key={`${date}${datePrefix(date)}`}
												value={date}>{`${date}${datePrefix(date)}`}</MenuItem>
										);
									})}
								</Select>
							</FormControl>
							<InputLabel className={`${classes.labelMonth} ${classes.text}`} id="month">
								{EVERY_MONTH}
							</InputLabel>
							{!isDynamicSelected && (
								<TimeField
									scheduleType={scheduleType}
									className={classes.timeField}
									selectedDate={selectedDate}
									handleDateChange={handleDateChange}
								/>
							)}
						</Box>
					</MuiPickersUtilsProvider>
				);
			case QUARTERLY:
				return (
					<Quarter
						scheduleType={scheduleType}
						selectedDate={selectedDate}
						handleDateChange={handleDateChange}
						selectQuarter={selectQuarter}
						setSelectQuarter={setSelectQuarter}
						dateSelect={dateSelect}
						handleDateSelect={handleDateSelect}
						setCronExpression={setCronExpression}
						isDynamicSelected={isDynamicSelected}
					/>
				);
			default:
				return null;
		}
	};
	return <Box>{renderSwitch(scheduleType)}</Box>;
}

Schedule.propTypes = {
	scheduleType: PropTypes.string,
	setSelectedDate: PropTypes.func.isRequired,
	selectedDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
	weeksSelected: PropTypes.string.isRequired,
	setWeeksSelected: PropTypes.func.isRequired,
	// dateSelect: PropTypes.number.isRequired,
	setDateSelect: PropTypes.func.isRequired,
	selectQuarter: PropTypes.number.isRequired,
	setSelectQuarter: PropTypes.func.isRequired,
	setCronExpression: PropTypes.func.isRequired,
	weeks: PropTypes.shape({})
};

export default Schedule;
